import { useHandler } from "@redotech/react-util/hook";
import {
  rcoCheckoutProbabilitySegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { CheckoutProbabilitySegment } from "@redotech/redo-model/checkouts/checkout-tree";
import { checkoutProbabilityExpressionEqual } from "@redotech/redo-model/checkouts/checkout-tree-equal";
import {
  DefaultCheckoutProbabilityBucket,
  defaultCheckoutProbabilityExpressions,
} from "@redotech/redo-model/checkouts/segments";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

const bucketLabels: Record<DefaultCheckoutProbabilityBucket, string> = {
  [DefaultCheckoutProbabilityBucket.COLD]: "cold",
  [DefaultCheckoutProbabilityBucket.COOL]: "cool",
  [DefaultCheckoutProbabilityBucket.WARM]: "warm",
  [DefaultCheckoutProbabilityBucket.HOT]: "hot",
};

function getCurrentBucketFromExpressions(
  expressions: CheckoutProbabilitySegment["expressions"],
): DefaultCheckoutProbabilityBucket {
  for (const bucket of Object.values(DefaultCheckoutProbabilityBucket)) {
    if (typeof bucket !== "number") {
      continue;
    }

    const bucketExpressions = defaultCheckoutProbabilityExpressions[bucket];
    if (checkoutProbabilityExpressionEqual(expressions, bucketExpressions)) {
      return bucket;
    }
  }

  return DefaultCheckoutProbabilityBucket.WARM;
}

export const CheckoutProbabilityInspectorContent = memo(
  function CheckoutProbabilityInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const segment = useMemo(
      () => rcoCheckoutProbabilitySegmentSchema.parse(step.segment),
      [step],
    );

    const currentBucket = useMemo(
      () => getCurrentBucketFromExpressions(segment.expressions),
      [segment.expressions],
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Condition
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          Checkout probability score is {bucketLabels[currentBucket]}
        </Text>
      </Flex>
    );
  },
);

export const EditableCheckoutProbabilityInspectorContent = memo(
  function EditableCheckoutProbabilityInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const segment = useMemo(
      () => rcoCheckoutProbabilitySegmentSchema.parse(step.segment),
      [step],
    );

    const currentBucket = useMemo(
      () => getCurrentBucketFromExpressions(segment.expressions),
      [segment.expressions],
    );

    const bucketOptions: RedoListItem<DefaultCheckoutProbabilityBucket>[] =
      useMemo(
        () => [
          {
            type: "text",
            id: DefaultCheckoutProbabilityBucket.COLD,
            text: bucketLabels[DefaultCheckoutProbabilityBucket.COLD],
            value: DefaultCheckoutProbabilityBucket.COLD,
          },
          {
            type: "text",
            id: DefaultCheckoutProbabilityBucket.COOL,
            text: bucketLabels[DefaultCheckoutProbabilityBucket.COOL],
            value: DefaultCheckoutProbabilityBucket.COOL,
          },
          {
            type: "text",
            id: DefaultCheckoutProbabilityBucket.WARM,
            text: bucketLabels[DefaultCheckoutProbabilityBucket.WARM],
            value: DefaultCheckoutProbabilityBucket.WARM,
          },
          {
            type: "text",
            id: DefaultCheckoutProbabilityBucket.HOT,
            text: bucketLabels[DefaultCheckoutProbabilityBucket.HOT],
            value: DefaultCheckoutProbabilityBucket.HOT,
          },
        ],
        [],
      );

    const selectedBucketOption = useMemo(
      () => bucketOptions.find((option) => option.value === currentBucket),
      [bucketOptions, currentBucket],
    );

    const handleBucketChange = useHandler(
      (args: { item: RedoListItem<DefaultCheckoutProbabilityBucket> }) => {
        const newBucket = args.item.value;
        const newExpressions = defaultCheckoutProbabilityExpressions[newBucket];

        setStep({
          ...step,
          segment: { ...segment, expressions: newExpressions },
        });
      },
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <RedoSingleSelectDropdownInput<DefaultCheckoutProbabilityBucket>
          label="Checkout probability score"
          options={bucketOptions}
          optionSelected={handleBucketChange}
          selectedItem={selectedBucketOption}
          size="xs"
        />
      </Flex>
    );
  },
);
